import { contracts, type Permission, type WorkspaceRoutes } from '@immo/api-contract';
import { mayCall, type PermissionSource } from '@/lib/permissions/route-gate';

export type QuickNavEntry = {
  readonly key: string;
  // Resolves under `pages.shell.nav`, the dictionary the sidebar reads too —
  // but NOT necessarily the same key. The sidebar names these destinations by
  // their place in the funnel („Immobilien", „Akquise-Pipeline",
  // „Eigentümer-Leads"); the palette names the search domain the member is
  // jumping into, matching the group headings the results will carry.
  readonly labelKey: string;
  readonly to: (routes: WorkspaceRoutes) => string;
  // The section only appears for a member holding this permission. An entry
  // without one is reachable by every member.
  //
  // These gates name the destination's own list endpoint, not
  // the sidebar rows' — those four rows carry no gate at all, because the reads
  // are in MEMBER_IMPLICIT_PERMISSIONS today. Gating on the endpoint's literal
  // is what makes the entry disappear exactly when the page behind it would
  // start refusing the member.
  readonly gate?: PermissionSource;
};

/**
 * The „Schnell springen" sections, in the fixed order the design pins.
 *
 * „Dokumente" is deliberately absent although the PRD's copy block lists it:
 * there is no workspace-level documents route to navigate to (documents are only
 * ever listed under an Objekt), and pointing the entry at the Objekte list would
 * make the label lie about where you land. Recorded as an override on IMM-448.
 */
export const quickNavEntries: ReadonlyArray<QuickNavEntry> = [
  {
    key: 'contacts',
    labelKey: 'contacts',
    to: (routes) => routes.contacts(),
    gate: contracts.contacts.workspaceList,
  },
  {
    key: 'properties',
    labelKey: 'properties',
    to: (routes) => routes.properties(),
    gate: contracts.properties.workspaceList,
  },
  {
    key: 'deals',
    labelKey: 'deals',
    to: (routes) => routes.deals(),
    gate: contracts.deals.workspaceList,
  },
  {
    key: 'leads',
    labelKey: 'leads',
    to: (routes) => routes.leads(),
    gate: contracts.leads.list,
  },
  // Settings carries no gate for the same reason the sidebar's settings row
  // carries none: the page itself is reachable by every member and its
  // sub-navigation gates the individual surfaces.
  { key: 'settings', labelKey: 'settings', to: (routes) => routes.settings() },
];

/** The entries a member holding `permissions` may reach, in catalog order. */
export function visibleQuickNavEntries(permissions: ReadonlyArray<Permission>): ReadonlyArray<QuickNavEntry> {
  return quickNavEntries.filter((entry) => entry.gate === undefined || mayCall(permissions, entry.gate));
}
